import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import {
  Alert,
  Alerts,
  Button,
  ButtonAction,
  Col,
  Container,
  FiltersCustom,
  PageContent,
  Pagination,
  Spinner,
  StatusBadge,
  Table,
} from 'components';
import { isLoading, globalPropTypes, checkPermissions, useFormFields } from 'helpers';
import { formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadIpmaYcReportsCreator,
  loadGlobalCountriesCreator,
  loadGlobalAvailableYCCreator,
  setIpmaYcReportsPageCreator,
  setIpmaYcReportsSortCreator,
  setIpmaYcReportsFiltersCreator,
  exportYcMembersCreator,
  exportYcContactPeopleCreator,
} from 'store/actionsCreators';
import { StyledRow, Actions } from '../Reports.styled';
import TabActionPortal from '../TabActionPortal';
import { ExportActions } from './YcReports.styled';

/**
 * The yearly reports the Young Crews sent in, as the YC Management Board sees
 * them.
 *
 * Built the same way as the MA reports of the tab next door - the same filter
 * panel above the same table - because the two sit under one section and a
 * reader moving between them should not have to learn a second screen.
 *
 * Drafts are deliberately absent: a report reaches this list the moment it is
 * sent and stays on it afterwards, so the board can read back what it decided
 * in earlier years next to what is waiting now.
 */
const YcReports = ({
  loading,
  list,
  countries,
  youngCrews,
  loadList,
  loadGlobalCountries,
  loadGlobalAvailableYC,
  setPage,
  setSort,
  setFilters,
  exportMembers,
  exportContactPeople,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems, statusOptions } = list;
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState(sort);

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: ['ipma_yc_report_view'] }, userPermissions || []),
    '/ipma',
    'page.ipma.ycReports.accessDenied',
  );

  const [filterFields, handleFilterFieldChange] = useFormFields({
    country: null,
    youngCrew: null,
    year: '',
    status: null,
  });

  const filtersProps = {
    country: {
      type: 'select',
      name: 'country',
      id: 'f-country',
      label: t('page.ipma.reports.country'),
      placeholder: t('forms.countryPlaceholder'),
      options: countries,
    },
    youngCrew: {
      // The panel picks a Young Crew off the list, so the API filters by id.
      type: 'select',
      name: 'youngCrewId',
      id: 'f-youngCrew',
      label: t('page.ipma.ycReports.youngCrew'),
      placeholder: t('page.ipma.ycReports.youngCrewPlaceholder'),
      options: youngCrews,
    },
    year: {
      type: 'input',
      name: 'year',
      id: 'f-year',
      label: t('page.ipma.ycReports.year'),
      placeholder: t('page.ipma.ycReports.yearPlaceholder'),
    },
    status: {
      type: 'select',
      name: 'status',
      id: 'f-status',
      label: t('page.ipma.ycReports.status'),
      placeholder: t('forms.statusPlaceholder'),
      options: statusOptions,
    },
  };

  useEffect(() => {
    if (!allowed) return;

    if (!countries.length) {
      loadGlobalCountries();
    }

    if (!youngCrews.length) {
      loadGlobalAvailableYC();
    }
  }, [allowed]);

  // The filter panel applies itself once on mount, so this covers the first
  // load as well as every change of a filter or of the sorted column.
  useEffect(() => {
    if (!allowed) return;

    loadList();
  }, [allowed, filters, sort]);

  /** Clicking a column heading again turns the order around, as next door. */
  const handleSortBy = (name) => () => {
    const [currentSortName, currentSortStatus] = (sortBy || '').split('.');
    const direction = currentSortName === name && currentSortStatus === 'desc' ? 'asc' : 'desc';
    const newSort = `${name}.${direction}`;

    setSortBy(newSort);
    setSort(newSort);
  };

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  const renderTable = () => (
    <Table tablebreakpoint="lg">
      <Table.Head>
        <Table.Row>
          <Table.Heading sort={handleSortBy('youngCrew')}>
            {t('page.ipma.ycReports.youngCrew')}
          </Table.Heading>
          <Table.Heading sort={handleSortBy('country')}>
            {t('page.ipma.ycReports.country')}
          </Table.Heading>
          <Table.Heading sort={handleSortBy('year')}>{t('page.ipma.ycReports.year')}</Table.Heading>
          <Table.Heading>{t('page.ipma.ycReports.events')}</Table.Heading>
          <Table.Heading sort={handleSortBy('submittedAt')}>
            {t('page.ipma.ycReports.sentOn')}
          </Table.Heading>
          <Table.Heading sort={handleSortBy('status')}>
            {t('page.ipma.ycReports.status')}
          </Table.Heading>
          <Table.Heading>{t('page.ipma.ycReports.action')}</Table.Heading>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {loading ? (
          <Table.Row>
            <Table.Cell colSpan={7}>
              <Spinner />
            </Table.Cell>
          </Table.Row>
        ) : null}
        {!loading && !items.length ? (
          <Table.Row>
            <Table.Cell colSpan={7}>{t('global.noResultsFound')}</Table.Cell>
          </Table.Row>
        ) : null}
        {!loading && items.length
          ? items.map((item, index) => (
              <StyledRow key={item.id} isOdd={(index + 1) % 2 !== 0}>
                <Table.Cell style={{ fontWeight: 600, color: '#4F4E5C', maxWidth: '400px' }}>
                  <Table.Label>{t('page.ipma.ycReports.youngCrew')}:</Table.Label>
                  {item.youngCrew || item.youngCrewInitials || '-'}
                </Table.Cell>
                <Table.Cell style={{ fontWeight: 600, color: '#4F4E5C', maxWidth: '400px' }}>
                  <Table.Label>{t('page.ipma.ycReports.country')}:</Table.Label>
                  {item.country || '-'}
                </Table.Cell>
                <Table.Cell>
                  <Table.Label>{t('page.ipma.ycReports.year')}:</Table.Label>
                  {item.year}
                </Table.Cell>
                <Table.Cell>
                  <Table.Label>{t('page.ipma.ycReports.events')}:</Table.Label>
                  {item.eventsCount}
                </Table.Cell>
                <Table.Cell>
                  <Table.Label>{t('page.ipma.ycReports.sentOn')}:</Table.Label>
                  {item.submittedAt ? formatDateTime(item.submittedAt) : '-'}
                </Table.Cell>
                <Table.Cell>
                  <Table.Label>{t('page.ipma.ycReports.status')}:</Table.Label>
                  <StatusBadge status={item.status}>{item.statusLabel}</StatusBadge>
                </Table.Cell>
                <Table.Cell>
                  <Table.Label>{t('page.ipma.ycReports.action')}:</Table.Label>
                  <Actions>
                    <ButtonAction
                      as={Link}
                      to={`/ipma/yc-reports/${item.id}`}
                      title={
                        item.decidable ? t('page.ipma.ycReports.review') : t('global.readButton')
                      }
                    />
                  </Actions>
                </Table.Cell>
              </StyledRow>
            ))
          : null}
      </Table.Body>
    </Table>
  );

  return (
    <>
      {/* The exports belong to this tab, so they are rendered into the slot the
          Reports section keeps next to its title. */}
      <TabActionPortal>
        <ExportActions>
          <Button secondary type="button" onClick={exportMembers}>
            {t('page.ipma.ycReports.exportMembers')}
          </Button>
          <Button secondary type="button" onClick={exportContactPeople}>
            {t('page.ipma.ycReports.exportContactPeople')}
          </Button>
        </ExportActions>
      </TabActionPortal>
      <PageContent>
        <Container>
          {checkPermissions({ roles: ['ipma_yc_report_view'] }, userPermissions) ? (
            <>
              <Margin bottom={{ md: 48 }}>
                <Alerts type="ipma" />
              </Margin>
              <Col>
                <FiltersCustom
                  title={t('page.ma.peopleAdd.filterTitle')}
                  filters={filtersProps}
                  filterFields={filterFields}
                  handleFilterFieldChange={handleFilterFieldChange}
                  onFiltersApply={setFilters}
                />
              </Col>
              <Col>
                <Margin top="24">
                  {/* The empty result is an answer of its own, the way it is on
                      the MA reports next door. */}
                  {!loading && !items.length && !Object.keys(filters).length ? (
                    <Alert type="empty">{t('page.ipma.ycReports.empty')}</Alert>
                  ) : (
                    renderTable()
                  )}
                </Margin>
              </Col>
              <Col>
                <Pagination
                  styled
                  currentPage={page}
                  totalPages={pages}
                  totalItems={totalItems}
                  perPage={perPage}
                  onChange={(payload) => {
                    setPage(payload);
                    loadList();
                  }}
                />
              </Col>
            </>
          ) : (
            <Alert type="info">{t('page.forbidden.subtitle')}</Alert>
          )}
        </Container>
      </PageContent>
    </>
  );
};

YcReports.propTypes = {
  loading: PropTypes.bool.isRequired,
  list: PropTypes.shape({
    filters: PropTypes.shape({}),
    items: PropTypes.arrayOf(PropTypes.shape({})),
    page: PropTypes.number,
    pages: PropTypes.number,
    perPage: PropTypes.number,
    sort: PropTypes.string,
    totalItems: PropTypes.number,
    statusOptions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  youngCrews: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  loadList: PropTypes.func.isRequired,
  loadGlobalCountries: PropTypes.func.isRequired,
  loadGlobalAvailableYC: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  exportMembers: PropTypes.func.isRequired,
  exportContactPeople: PropTypes.func.isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
};

const mapStateToProps = ({ globals, ipma, user, loading }) => ({
  list: ipma.ycReports,
  loading: isLoading(loading, 'GLOBAL'),
  countries: globals.countries,
  youngCrews: globals.availableYC,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaYcReportsCreator()),
  loadGlobalCountries: () => dispatch(loadGlobalCountriesCreator()),
  loadGlobalAvailableYC: () => dispatch(loadGlobalAvailableYCCreator()),
  setPage: (payload) => dispatch(setIpmaYcReportsPageCreator(payload)),
  setSort: (payload) => dispatch(setIpmaYcReportsSortCreator(payload)),
  setFilters: (payload) => dispatch(setIpmaYcReportsFiltersCreator(payload)),
  exportMembers: () => dispatch(exportYcMembersCreator()),
  exportContactPeople: () => dispatch(exportYcContactPeopleCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(YcReports);
